/**
 * Tractor State — the console's vehicle model and its two data sources.
 *
 * The live source is `GET /v1/vehicles` (see configApi.ts): roster identity,
 * the operator-authored config record, the Jira summary, the server-derived
 * `operational_state` and the fleet rollup, all in one call. Every environment
 * with the API configured uses it and only it.
 *
 * `fleetSnapshot.json` — a committed join of the BETA tables (see
 * scripts/build_fleet_snapshot.py) — is what remains for isolated dev: a dev
 * server with no fleet_state config, so the page can be worked on offline. It
 * carries no config attributes and no `config_version`, which is exactly why
 * editing is unavailable in that mode: there is no version to echo back, so no
 * write could be made safe against a concurrent one. It is loaded through
 * `loadSnapshotFleet`, which keeps its 190 KB out of every deployed bundle.
 *
 * **Vehicles are keyed on the roster name** (`dl13`), not the Jira asset id
 * (`DL-13`). The roster name is the DynamoDB partition key, the PATCH key, and
 * the one identifier every vehicle has — the Jira id is derived from
 * `display_name`, which is unseeded on exactly the tractors this console exists
 * to surface. The two are not derivable from each other (`av6` is AV-DL-01,
 * `dl6` is AV-DL-06), so there is no third copy of that lookup table here.
 */
import { tokens } from "./tokens";
import {
  AttrValue,
  ChangeSet,
  ConfigSchema,
  ProductionReadiness,
  VehicleRow,
} from "./configTypes";

/** Jira "Current Tractor Operational Status" level (customfield_11617). */
export type OpStatus = "CRITICAL" | "MAJOR" | "MINOR";

export interface Ticket {
  key: string;
  summary: string;
  /** Operational status level; null for the many tickets that don't set it. */
  op: OpStatus | null;
  status: string | null;
  statusCategory: string | null;
  assignee: string;
  /** When the ticket was raised — how long the issue has persisted. */
  created: string | null;
  updated: string | null;
  isBlocker: boolean;
}

export interface Vehicle {
  /** Roster name, e.g. "dl13". Partition key, PATCH key, and route key. */
  vehicleName: string;
  /** Jira tractor_id, e.g. "DL-13"; null when display_name isn't seeded. */
  tractorId: string | null;
  /** Display name, e.g. "AV-DL-13"; null when not yet seeded. */
  displayName: string | null;
  customer: string | null;
  /** Roster lifecycle: "active" | "maintenance" | "decommissioned". */
  rosterStatus: string | null;
  /** The single reconciled status, computed server-side. Null in snapshot mode. */
  operationalState: string | null;
  /** True when any operator-authored config field is set on the row. */
  hasConfigRecord: boolean;
  /** Optimistic-concurrency token. Null = never edited, or snapshot mode. */
  configVersion: number | null;
  /** Server-computed readiness from fleet_state (blocker-driven). */
  readiness: "ready" | "grounded" | null;
  /**
   * Server-derived production readiness: the stored `production_ready` mark
   * checked against the rule table and Jira. Null in snapshot mode and on an
   * API that predates it, which renders as nothing rather than as "not set".
   */
  productionReadiness: ProductionReadiness | null;
  manualGround: boolean;
  /** Total open (not Done/Closed) tickets — includes non-operational noise. */
  openTicketCount: number;
  openBlockerCount: number;
  /**
   * Open tickets per Jira operational status — `{ CRITICAL: 1, MINOR: 2 }`.
   *
   * Precomputed by the poller and carried on *every* fleet row, unlike
   * `tickets`, which only the single-vehicle route sends. That difference is
   * the point: the grid can say what kind of trouble a tractor is in without
   * fetching every tractor's tickets.
   *
   * Empty for an asset written before the aggregate existed, which means "no
   * breakdown available" and not "no tickets" — `openTicketCount` remains
   * authoritative for the total.
   */
  openByOp: Record<string, number>;
  hasAsset: boolean;
  /** Open tickets for this vehicle, operational-status first. */
  tickets: Ticket[];
  /**
   * Every stored config attribute, addressed by schema field name. Deliberately
   * an untyped bag: the schema decides what's in here, so a field added to
   * tractor_config.py renders with no change on this side.
   */
  attrs: Record<string, AttrValue>;
  /** Change-sets, newest last. Only populated on the single-vehicle route. */
  history: ChangeSet[];
}

/** Fleet grid filter: "all" or a customer key present in the roster. */
export type Filter = string;
/** Sort order for the detail view's sibling rail. */
export type SortBy = "tickets" | "name" | "attention";

/**
 * Which name to surface — the "AV-DL-13" display name or the "dl13" roster
 * name. Not every tractor's display name is communicated consistently, so the
 * console lets ops flip between the two. Display falls back to the roster name
 * rather than showing a blank for an unseeded vehicle.
 */
export type NameMode = "display" | "roster";
export const primaryName = (v: Vehicle, mode: NameMode): string =>
  mode === "roster" ? v.vehicleName : (v.displayName ?? v.vehicleName);
export const secondaryName = (v: Vehicle, mode: NameMode): string =>
  mode === "roster" ? (v.displayName ?? "—") : v.vehicleName;

/**
 * Natural (human) name order: the embedded numbers sort numerically, so
 * dl8 < dl9 < dl10 < dl11 rather than the lexicographic dl10 < dl11 < dl6.
 * Matters for roster names (dl6, av10 — unpadded); display names (AV-DL-06)
 * happen to already be zero-padded.
 */
const compareNames = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/**
 * Fleet ordering for the detail view's sibling rail.
 *
 * "attention" preserves the order the API already sorted rows into (most
 * actionable first, per `operational_state.attention_rank`), so the console
 * doesn't re-derive a precedence the backend owns; it only applies when the
 * rows came from the API, since the snapshot has no operational state.
 *
 * The fleet table doesn't use this: it renders the API's order as-is and hands
 * every other ordering to its own sortable columns, which can sort by any field
 * rather than the three this comparator knows.
 */
export const vehicleComparator =
  (sortBy: SortBy, mode: NameMode) =>
  (a: Vehicle, b: Vehicle): number => {
    if (sortBy === "name") {
      return compareNames(primaryName(a, mode), primaryName(b, mode));
    }
    if (sortBy === "tickets") {
      return (
        b.openTicketCount - a.openTicketCount ||
        compareNames(primaryName(a, mode), primaryName(b, mode))
      );
    }
    return 0; // "attention" — keep the server's order.
  };

/**
 * One hue per severity, and the three read as a scale: red → orange → yellow,
 * hottest first. MINOR used to be grey, which made it indistinguishable from
 * "no severity at all" — grey is now reserved for exactly that (the neutral
 * chip an unknown status gets). Text colours are darkened past the fills for
 * contrast. The severity's *name* always travels with the colour, but where
 * differs by surface: the detail page prints it in the chip, while the fleet
 * row shows count-on-hue and carries the name in the chip's accessible label
 * and the hover card.
 */
export const OP_CONFIG: Record<
  OpStatus,
  { bg: string; color: string; border: string }
> = {
  CRITICAL: {
    bg: "rgba(229,72,77,0.1)",
    color: tokens.groundedText,
    border: "rgba(229,72,77,0.35)",
  },
  MAJOR: {
    bg: "rgba(255,150,0,0.12)",
    color: tokens.majorText,
    border: "rgba(255,150,0,0.4)",
  },
  MINOR: {
    bg: "rgba(255,205,0,0.14)",
    color: tokens.minorText,
    border: "rgba(255,205,0,0.5)",
  },
};

export const jiraUrl = (key: string) =>
  `https://aerovect.atlassian.net/browse/${key}`;

/** Jira operational-status severity, high (CRITICAL) to none. Drives ticket sorting. */
export const severityRank = (op: OpStatus | null): number =>
  op === "CRITICAL" ? 3 : op === "MAJOR" ? 2 : op === "MINOR" ? 1 : 0;

/** One severity and how many open tickets a tractor has at it. */
export interface OpBreakdown {
  op: OpStatus | string;
  count: number;
  /** False for a status this console has no colour for — see `OP_CONFIG`. */
  known: boolean;
}

/**
 * A tractor's severity breakdown, worst first.
 *
 * Ordered by `severityRank` for the statuses we know, with anything else after
 * them in alphabetical order. Unknown statuses are *kept*: the backend
 * aggregates by whatever value Jira holds precisely so a new one reaches the
 * console, and dropping it here would quietly hide tickets from a count the
 * reader is using to judge a tractor.
 */
export function opBreakdown(vehicle: Vehicle): OpBreakdown[] {
  return Object.entries(vehicle.openByOp)
    .map(([op, count]) => ({
      op,
      count,
      known: op in OP_CONFIG,
    }))
    .sort((a, b) => {
      const rank =
        severityRank(b.known ? (b.op as OpStatus) : null) -
        severityRank(a.known ? (a.op as OpStatus) : null);
      return rank !== 0 ? rank : a.op.localeCompare(b.op);
    });
}

/**
 * Open tickets carrying no operational status at all.
 *
 * The majority, usually — the Jira filter includes plenty that never had the
 * field set. Shown as a remainder rather than a severity, because "unclassified"
 * is not a level of badness and grouping it with the others would inflate what
 * the card appears to be reporting.
 */
export const unclassifiedCount = (vehicle: Vehicle): number =>
  Math.max(
    0,
    vehicle.openTicketCount -
      Object.values(vehicle.openByOp).reduce((sum, n) => sum + n, 0),
  );

/** Ticket sort controls in the per-tractor detail view. */
export type TicketSortField = "severity" | "date";
export type SortDir = "asc" | "desc";

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

const OP_RANK: Record<string, number> = { CRITICAL: 0, MAJOR: 1, MINOR: 2 };

interface ApiTicket {
  ticket_key?: string;
  key?: string;
  summary?: string;
  operational_status?: OpStatus | null;
  op?: OpStatus | null;
  status?: string | null;
  status_category?: string | null;
  statusCategory?: string | null;
  assignee?: string | null;
  created_ts?: string | null;
  created?: string | null;
  jira_updated_ts?: string | null;
  updated?: string | null;
  is_blocker?: boolean;
  isBlocker?: boolean;
}

/** Normalises both the API's snake_case tickets and the snapshot's camelCase. */
function toTicket(t: ApiTicket): Ticket {
  return {
    key: t.ticket_key ?? t.key ?? "",
    summary: t.summary || "",
    op: t.operational_status ?? t.op ?? null,
    status: t.status ?? null,
    statusCategory: t.status_category ?? t.statusCategory ?? null,
    assignee: t.assignee || "Unassigned",
    // Absent on tickets synced before the poller stored it, and on the
    // committed snapshot; the detail view falls back to the update's age.
    created: t.created_ts ?? t.created ?? null,
    updated: t.jira_updated_ts ?? t.updated ?? null,
    isBlocker: !!(t.is_blocker ?? t.isBlocker),
  };
}

/**
 * The API's severity breakdown, defended against a row that predates it.
 *
 * Coerces rather than trusts: this arrives from DynamoDB via JSON, where a
 * count can surface as a string, and the map is absent entirely on an asset the
 * poller has not recomputed yet.
 */
function toOpCounts(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== "object") return {};
  const counts: Record<string, number> = {};
  for (const [status, value] of Object.entries(
    raw as Record<string, unknown>,
  )) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) counts[status] = n;
  }
  return counts;
}

/** The same shape, counted from a ticket list the client already holds. */
function countByOp(tickets: Ticket[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const ticket of tickets) {
    if (!ticket.op) continue;
    counts[ticket.op] = (counts[ticket.op] ?? 0) + 1;
  }
  return counts;
}

/** Operational status first, then newest. Matches the detail view's default. */
const byOperationalThenRecent = (a: Ticket, b: Ticket) =>
  (OP_RANK[a.op ?? ""] ?? 9) - (OP_RANK[b.op ?? ""] ?? 9) ||
  String(b.updated).localeCompare(String(a.updated));

/**
 * Keys the API derives or manages structurally, rather than config attributes
 * the schema addresses. Everything else on the row lands in `attrs`, so a new
 * field in tractor_config.py needs no edit here.
 */
const DERIVED_KEYS = new Set([
  "tractor_id",
  "operational_state",
  "has_config_record",
  "has_asset",
  "readiness",
  "production_readiness",
  "open_ticket_count",
  "open_blocker_count",
  "open_by_op",
  "manual_ground",
  "open_tickets",
  "config_history",
]);

function toAttrs(row: VehicleRow): Record<string, AttrValue> {
  const attrs: Record<string, AttrValue> = {};
  for (const [key, value] of Object.entries(row)) {
    if (DERIVED_KEYS.has(key)) continue;
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      Array.isArray(value)
    ) {
      attrs[key] = value as AttrValue;
    }
  }
  return attrs;
}

/**
 * Whether a roster row is a test rig or simulator rather than a tractor.
 *
 * Read off the `vehicle_kind` config field, which fleet-admins set; absent
 * means tractor, so nothing is hidden until someone marks it. Explicit rather
 * than inferred from "no display name / unconfigured": a real tractor
 * mid-onboarding looks exactly like that, and would vanish from the grid just
 * when someone is trying to find it.
 */
export const isTestRig = (v: Vehicle): boolean =>
  typeof v.attrs.vehicle_kind === "string" &&
  v.attrs.vehicle_kind !== "TRACTOR";

/** The derived readiness as sent, or null for anything that isn't its shape. */
function toReadiness(raw: unknown): ProductionReadiness | null {
  if (!raw || typeof raw !== "object") return null;
  const { value, blockers } = raw as Record<string, unknown>;
  if (typeof value !== "string") return null;
  return {
    value,
    blockers: Array.isArray(blockers)
      ? blockers.filter((b): b is string => typeof b === "string")
      : [],
  };
}

/** One `/v1/vehicles` row -> the console's model. */
export function vehicleFromRow(row: VehicleRow): Vehicle {
  const tickets = ((row.open_tickets ?? []) as ApiTicket[])
    .map(toTicket)
    .sort(byOperationalThenRecent);
  return {
    vehicleName: row.vehicle_name,
    tractorId: row.tractor_id ?? null,
    displayName: (row.display_name as string | null) ?? null,
    customer: (row.customer as string | null) ?? null,
    rosterStatus: (row.status as string | null) ?? null,
    operationalState: row.operational_state ?? null,
    hasConfigRecord: !!row.has_config_record,
    configVersion:
      typeof row.config_version === "number" ? row.config_version : null,
    readiness: row.readiness ?? null,
    productionReadiness: toReadiness(row.production_readiness),
    manualGround: !!row.manual_ground,
    // The grid sends counts without tickets; the detail route sends both.
    openTicketCount: Number(row.open_ticket_count ?? tickets.length),
    openBlockerCount: Number(row.open_blocker_count ?? 0),
    openByOp: toOpCounts(row.open_by_op),
    hasAsset: !!row.has_asset,
    tickets,
    attrs: toAttrs(row),
    history: row.config_history ?? [],
  };
}

interface SnapshotTractor {
  id: string;
  name: string;
  rosterName: string;
  customer: string | null;
  rosterStatus: string | null;
  readiness: "ready" | "grounded" | null;
  manualGround: boolean;
  openTicketCount: number;
  openBlockerCount: number;
  hasAsset: boolean;
  tickets: ApiTicket[];
  /**
   * The config record as it stood when the snapshot was built, so isolated dev
   * renders the panel with values rather than a page of em-dashes. Optional:
   * a fixture built before the record was included still loads.
   */
  config?: Record<string, AttrValue>;
  configVersion?: number | null;
  hasConfigRecord?: boolean;
}

/**
 * Snapshot row -> the same model, read-only.
 *
 * The config record rides along for display, but this mode still never edits:
 * the page has no token here, and a snapshot's `config_version` describes the
 * fixture's build time, not the row as it stands — exactly the assertion an
 * edit must not send.
 */
function vehicleFromSnapshot(t: SnapshotTractor): Vehicle {
  return {
    vehicleName: t.rosterName,
    tractorId: t.id,
    displayName: t.name,
    customer: t.customer,
    rosterStatus: t.rosterStatus,
    operationalState: null,
    hasConfigRecord: !!t.hasConfigRecord,
    configVersion: typeof t.configVersion === "number" ? t.configVersion : null,
    readiness: t.readiness,
    // Snapshot mode has no derived readiness, like it has no operational
    // state: null renders nothing, not "not set".
    productionReadiness: null,
    manualGround: t.manualGround,
    openTicketCount: t.openTicketCount,
    openBlockerCount: t.openBlockerCount,
    // Derived here rather than stored in the fixture: live gets this figure
    // precomputed by the poller, and a snapshot that reported it differently
    // would make the grid look wrong in exactly the mode used to develop it.
    openByOp: countByOp(t.tickets.map(toTicket)),
    hasAsset: t.hasAsset,
    tickets: t.tickets.map(toTicket).sort(byOperationalThenRecent),
    attrs: t.config ?? {},
    history: [],
  };
}

/** The committed fleet: every rostered AV-DL tractor, ticketed or not. */
export interface SnapshotFleet {
  vehicles: Vehicle[];
  /** When the snapshot was built, so the page can say how old it is. */
  generatedAt: string;
}

/**
 * Load the committed snapshot — isolated dev only.
 *
 * The import is deliberately dynamic and deliberately inside the `NODE_ENV`
 * branch. `process.env.NODE_ENV` is substituted at build time, so a production
 * build compiles this to a dead branch, webpack never follows the import, and
 * the 190 KB of JSON is left out of the bundle entirely rather than shipped as
 * a chunk nobody fetches. That is the mechanism, and the intent behind it: an
 * environment that can reach the API has no business carrying a stale copy of
 * the fleet it could fall back to.
 *
 * Returns null when there is no snapshot to load, which is every deployed
 * build. Callers surface that as a configuration error, never as an empty fleet.
 */
export async function loadSnapshotFleet(): Promise<SnapshotFleet | null> {
  if (process.env.NODE_ENV !== "production") {
    const { default: snapshot } = await import("./fleetSnapshot.json");
    return {
      vehicles: (snapshot.tractors as SnapshotTractor[]).map(
        vehicleFromSnapshot,
      ),
      generatedAt: snapshot.generatedAt as string,
    };
  }
  return null;
}

/**
 * Load the committed config *schema* — isolated dev only, same mechanism.
 *
 * Without it, isolated dev could not render the largest thing on the detail
 * route at all: the config panel is generated entirely from the schema, so with
 * none loaded it says "the schema isn't loaded" and the change history, the
 * capability matrix and the table columns all come up empty. "So the page can
 * be worked on offline" was the point of snapshot mode, and the record was the
 * one part of the page it excluded.
 *
 * Distinct from the fleet snapshot in what it risks. This is *vocabulary*, not
 * data — labels, groups and enum options, with no tractor's values in it — so a
 * stale copy cannot misreport a fleet. It still cannot reach a deployed bundle:
 * the dynamic import sits inside the same pruned `NODE_ENV` branch, and the
 * page only asks for it on the path that has no API to ask instead.
 *
 * Regenerate from the backend's own dictionary, which is the only source:
 *
 *     cd backend/lambda/fleet_state && python3 -c "import json, tractor_config, \
 *       operational_state; p = tractor_config.schema_payload({'fleet-admin'}); \
 *       p.update(operational_state.state_payload()); p['roles'] = ['fleet-admin']; \
 *       print(json.dumps(p, indent=2))" \
 *       > ../../../frontend/src/pages/TractorState/schemaSnapshot.json
 *
 * It is resolved for a **fleet-admin** so every affordance is visible offline;
 * what a given operator may actually write is decided by the live API, which is
 * the only place `can_edit` is ever honoured for a real write.
 */
export async function loadSnapshotSchema(): Promise<ConfigSchema | null> {
  if (process.env.NODE_ENV !== "production") {
    const { default: schema } = await import("./schemaSnapshot.json");
    return schema as unknown as ConfigSchema;
  }
  return null;
}
